import { useMemo, useState } from 'react';
import { Badge, Kaart, Knop, Leeg, Modaal, PaginaKop, Paginering, SkeletTabel, Veld, useToast } from '../components/ui';
import { useAuth } from '../lib/auth';
import { fmtDatumTijd, initialen } from '../lib/format';
import { stuurMailStil } from '../lib/mail';
import { useProfiles, useRollen, useUpdateProfile } from '../lib/queries';
import type { Profile, Status } from '../lib/types';

const STATUS_TOON: Record<Status, { label: string; klasse: string }> = {
  actief: { label: 'actief', klasse: 'bg-veld-100 text-veld-800 border-veld-200' },
  aangevraagd: { label: 'wacht op goedkeuring', klasse: 'bg-amber-50 text-amber-800 border-amber-200' },
  geweigerd: { label: 'geweigerd', klasse: 'bg-red-50 text-red-900 border-red-200' },
  gearchiveerd: { label: 'gearchiveerd', klasse: 'bg-krijt-100 text-krijt-500 border-krijt-200' }
};

export default function Gebruikers() {
  const { profile: ik, demo, herlaadProfiel } = useAuth();
  const profielen = useProfiles();
  const rollen = useRollen();
  const bijwerken = useUpdateProfile();
  const meld = useToast();

  const [zoek, setZoek] = useState('');
  const [rolFilter, setRolFilter] = useState('');
  const [toonGearchiveerd, setToonGearchiveerd] = useState(false);
  const [pagina, setPagina] = useState(0);
  const [perPagina, setPerPagina] = useState(10);
  const [bewerken, setBewerken] = useState<Profile | null>(null);
  const [uitnodigen, setUitnodigen] = useState(false);

  const alle = profielen.data ?? [];

  const gefilterd = useMemo(() => {
    const term = zoek.trim().toLowerCase();
    return alle
      .filter((p) => (toonGearchiveerd ? true : p.status !== 'gearchiveerd'))
      .filter((p) => (rolFilter ? p.role === rolFilter : true))
      .filter((p) =>
        term ? p.name.toLowerCase().includes(term) || (p.username ?? '').toLowerCase().includes(term) : true
      );
  }, [alle, zoek, rolFilter, toonGearchiveerd]);

  const wachtenden = alle.filter((p) => p.status === 'aangevraagd');
  const paginas = Math.max(1, Math.ceil(gefilterd.length / perPagina));
  const huidige = Math.min(pagina, paginas - 1);
  const zichtbaar = gefilterd.slice(huidige * perPagina, (huidige + 1) * perPagina);

  async function zet(profiel: Profile, velden: Parameters<typeof bijwerken.mutateAsync>[0]['velden'], melding: string) {
    try {
      await bijwerken.mutateAsync({ id: profiel.id, velden });
      meld(melding);
      if (ik?.id === profiel.id) await herlaadProfiel();

      /* Wie voor het eerst wordt vrijgegeven, hoort dat te horen. Gaat de mail
         niet weg, dan is de goedkeuring alsnog gelukt; dat zeggen we er dan
         bij in plaats van de handeling te laten mislukken. */
      if (velden.status === 'actief' && profiel.status === 'aangevraagd') {
        const fout = await stuurMailStil('account_goedgekeurd', { profielId: profiel.id });
        if (fout) meld(`${profiel.name} mag meedoen, maar de mail ging niet weg: ${fout}`, true);
      }
    } catch (err) {
      meld(err instanceof Error ? err.message : 'Opslaan mislukte.', true);
    }
  }

  if (demo) {
    return (
      <>
        <PaginaKop titel="Gebruikersbeheer" />
        <Kaart>
          <Leeg tekst="In demomodus zijn er geen echte accounts. Verbind de app met Supabase." ikoon="sleutel" />
        </Kaart>
      </>
    );
  }

  return (
    <>
      <PaginaKop
        titel="Gebruikersbeheer"
        onder="Een account is een speler: wie hier staat, staat ook op de ranglijst. Iedereen meldt zich zelf aan; jij bepaalt wie meedoet. Archiveren haalt iemand uit de keuzelijsten, maar zijn potjes blijven staan."
        rechts={
          <Knop variant="primair" onClick={() => setUitnodigen(true)}>
            + Gebruiker
          </Knop>
        }
      />

      {wachtenden.length ? (
        <div className="mb-3.5 border-2 border-krijt-900 bg-signaal/25 p-4">
          <p className="kop mb-3 text-[15px]">
            {wachtenden.length} {wachtenden.length === 1 ? 'aanvraag' : 'aanvragen'} open
          </p>
          <div className="flex flex-col gap-2">
            {wachtenden.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-3 border border-krijt-300 bg-white px-3 py-2.5">
                <Bolletje naam={p.name} />
                <span className="font-semibold">{p.name}</span>
                {p.username ? <span className="text-sm text-krijt-500">@{p.username}</span> : null}
                <span className="ml-auto flex gap-2">
                  <Knop klein variant="primair" onClick={() => void zet(p, { status: 'actief' }, `${p.name} mag meedoen.`)}>
                    Goedkeuren
                  </Knop>
                  <Knop klein variant="gevaar" onClick={() => void zet(p, { status: 'geweigerd' }, `${p.name} is geweigerd.`)}>
                    Weigeren
                  </Knop>
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <Kaart>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input
            className="veld w-auto min-w-56 flex-1"
            placeholder="Zoeken op naam of gebruikersnaam"
            value={zoek}
            onChange={(e) => {
              setZoek(e.target.value);
              setPagina(0);
            }}
          />
          <select
            className="veld w-auto"
            value={rolFilter}
            onChange={(e) => {
              setRolFilter(e.target.value);
              setPagina(0);
            }}
          >
            <option value="">Alle rollen</option>
            {(rollen.data ?? []).map((r) => (
              <option key={r.sleutel} value={r.sleutel}>
                {r.label}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-krijt-600">
            <input
              type="checkbox"
              checked={toonGearchiveerd}
              onChange={(e) => {
                setToonGearchiveerd(e.target.checked);
                setPagina(0);
              }}
            />
            Toon gearchiveerd
          </label>
        </div>

        {profielen.isLoading ? (
          <SkeletTabel rijen={8} kolommen={6} />
        ) : zichtbaar.length ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-y-2 border-krijt-900 bg-krijt-50">
                  {['Naam', 'Rol', 'Status', 'Laatst ingelogd', 'Laatste activiteit', ''].map((k, i) => (
                    <th
                      key={k}
                      className={`opschrift whitespace-nowrap px-3 py-2.5 text-krijt-600 ${i === 5 ? 'text-right' : 'text-left'}`}
                    >
                      {k}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {zichtbaar.map((p) => (
                  <tr key={p.id} className="border-b border-krijt-200 hover:bg-krijt-50">
                    <td className="px-3 py-2.5">
                      <span className="flex items-center gap-2.5">
                        <Bolletje naam={p.name} />
                        <span className="min-w-0">
                          <span className="block truncate font-semibold">
                            {p.name}
                            {ik?.id === p.id ? <span className="ml-1.5 text-xs text-krijt-500">(jij)</span> : null}
                          </span>
                          <span className="block truncate text-xs text-krijt-500">
                            {p.username ? `@${p.username}` : 'geen gebruikersnaam'}
                          </span>
                        </span>
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge toon={p.role === 'admin' ? 'beheer' : 'neutraal'}>
                        {(rollen.data ?? []).find((r) => r.sleutel === p.role)?.label ?? p.role}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex border px-2 py-0.5 text-[11.5px] font-semibold ${STATUS_TOON[p.status].klasse}`}>
                        {STATUS_TOON[p.status].label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-krijt-600">
                      {p.lastLoginAt ? fmtDatumTijd(p.lastLoginAt) : '–'}
                    </td>
                    <td className="px-3 py-2.5 text-krijt-600">
                      {p.lastActivityAt ? fmtDatumTijd(p.lastActivityAt) : '–'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right">
                      <span className="flex justify-end gap-2">
                        <Knop klein onClick={() => setBewerken(p)}>
                          Bewerken
                        </Knop>
                        {p.status === 'gearchiveerd' ? (
                          <Knop klein onClick={() => void zet(p, { status: 'actief' }, `${p.name} doet weer mee.`)}>
                            Terughalen
                          </Knop>
                        ) : (
                          <Knop
                            klein
                            variant="gevaar"
                            disabled={ik?.id === p.id}
                            title={ik?.id === p.id ? 'Jezelf archiveren kan niet' : undefined}
                            onClick={() => void zet(p, { status: 'gearchiveerd' }, `${p.name} is gearchiveerd.`)}
                          >
                            Archiveren
                          </Knop>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Leeg tekst="Geen gebruikers die aan dit filter voldoen." ikoon="zoek" />
        )}

        <Paginering
          totaal={gefilterd.length}
          perPagina={perPagina}
          pagina={huidige}
          onPagina={setPagina}
          onPerPagina={(n) => {
            setPerPagina(n);
            setPagina(0);
          }}
        />
      </Kaart>

      {bewerken ? (
        <GebruikerModaal
          profiel={bewerken}
          onSluit={() => setBewerken(null)}
          onBewaard={async () => {
            if (ik?.id === bewerken.id) await herlaadProfiel();
          }}
        />
      ) : null}

      {uitnodigen ? <UitnodigenModaal onSluit={() => setUitnodigen(false)} /> : null}
    </>
  );
}

function Bolletje({ naam }: { naam: string }) {
  return (
    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-krijt-950 text-[12px] font-bold text-white">
      {initialen(naam)}
    </span>
  );
}

function GebruikerModaal({
  profiel,
  onSluit,
  onBewaard
}: {
  profiel: Profile;
  onSluit: () => void;
  onBewaard: () => Promise<void>;
}) {
  const rollen = useRollen();
  const bijwerken = useUpdateProfile();
  const meld = useToast();
  const [naam, setNaam] = useState(profiel.name);
  const [gebruikersnaam, setGebruikersnaam] = useState(profiel.username ?? '');
  const [rol, setRol] = useState(profiel.role);
  const [fout, setFout] = useState<string | null>(null);

  async function bewaar() {
    if (!naam.trim()) {
      setFout('Vul een naam in.');
      return;
    }
    try {
      await bijwerken.mutateAsync({
        id: profiel.id,
        velden: { name: naam.trim(), username: gebruikersnaam.trim().toLowerCase() || null, role: rol }
      });
      meld('Gebruiker bijgewerkt.');
      await onBewaard();
      onSluit();
    } catch (err) {
      setFout(err instanceof Error ? err.message : 'Opslaan mislukte.');
    }
  }

  return (
    <Modaal
      titel="Gebruiker bewerken"
      onSluit={onSluit}
      voet={
        <>
          <Knop onClick={onSluit}>Annuleren</Knop>
          <Knop variant="primair" onClick={() => void bewaar()} disabled={bijwerken.isPending}>
            {bijwerken.isPending ? 'Bezig…' : 'Opslaan'}
          </Knop>
        </>
      }
    >
      {fout ? <div className="mb-3 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">{fout}</div> : null}

      <Veld label="Naam">
        <input className="veld" value={naam} onChange={(e) => setNaam(e.target.value)} />
      </Veld>

      <Veld label="Gebruikersnaam" help="Waarmee anderen deze persoon voor een poule uitnodigen. Kleine letters, cijfers, punt of streepje.">
        <input
          className="veld"
          value={gebruikersnaam}
          placeholder="bijv. imre"
          onChange={(e) => setGebruikersnaam(e.target.value)}
        />
      </Veld>

      <Veld label="Rol">
        <select className="veld" value={rol} onChange={(e) => setRol(e.target.value)}>
          {(rollen.data ?? []).map((r) => (
            <option key={r.sleutel} value={r.sleutel}>
              {r.label}
            </option>
          ))}
        </select>
      </Veld>

      <p className="text-xs text-krijt-500">
        E-mailadres en wachtwoord horen bij Supabase Auth; die pas je aan in het dashboard.
      </p>
    </Modaal>
  );
}

function UitnodigenModaal({ onSluit }: { onSluit: () => void }) {
  const meld = useToast();
  const link = `${window.location.origin}/aanmelden`;

  return (
    <Modaal
      titel="Iemand laten meedoen"
      onSluit={onSluit}
      voet={<Knop onClick={onSluit}>Sluiten</Knop>}
    >
      <p className="mb-3 text-sm leading-relaxed text-krijt-600">
        Accounts worden niet door jou aangemaakt maar door de persoon zelf: die meldt zich aan met e-mailadres,
        wachtwoord en een gebruikersnaam. Daarna verschijnt de aanvraag hier en geef je hem vrij.
      </p>
      <div className="flex items-center gap-2">
        <input className="veld" readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
        <Knop
          onClick={() => {
            void navigator.clipboard.writeText(link);
            meld('Link gekopieerd.');
          }}
        >
          Kopiëren
        </Knop>
      </div>
    </Modaal>
  );
}
